import {
    Box,
    HStack,
    Skeleton,
    SkeletonCircle,
    Stack,
    Switch,
    Text,
    VStack,
} from '@chakra-ui/react';
import { FormControl, FormLabel } from 'components/v4';
import NumberField from 'components/forms/new/formikFormFields/NumberField';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import { Field } from 'formik';
import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import FormBase from '@/components/forms/new/FormBase';

import { toast } from 'react-toastify';
import walkthroughIds from '../../../accounts/admins/walkthroughIds';
import { useQuery } from 'hooks/useCustomQuery';
import {
    getPasswordPolicy,
    postPasswordPolicy,
    updatePasswordPolicy,
} from 'API/PasswordPolicy';
import FieldError from 'components/forms/new/FieldError';
import {
    inputStyles,
    MINNUMBEROFDAYS,
    MAXNUMBEROFDAYS,
    MINREUSELIMIT,
    MAXREUSELIMIT,
    MINMINPASSWORDAGE,
    MAXMINPASSWORDAGE,
} from './constants.js';

// Field Components following the FormBase pattern
const PasswordExpirySwitchField = ({
    isChecked,
    isDisabled,
    onChange,
    walkthroughId,
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    return (
        <FormControl sx={{ margin: '0 !important' }}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling(true)}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Password Expiration Settings
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        Control whether passwords expire or remain valid
                        indefinitely.
                    </Text>
                </Box>
                <Box flex="1" minWidth="450px">
                    <VStack width="100%" alignItems="normal" marginTop="20px">
                        <HStack spacing={3} alignItems="center">
                            <Switch
                                isChecked={isChecked}
                                isDisabled={isDisabled}
                                onChange={(e) => onChange(e.target.checked)}
                                data-walkthroughid={walkthroughId}
                            />
                            <FormLabel
                                sx={{ marginBottom: '6px !important' }}
                                fontSize="sm"
                                display="flex"
                                alignItems="center">
                                Enable Password Expiry
                            </FormLabel>
                        </HStack>
                    </VStack>
                </Box>
            </Stack>
        </FormControl>
    );
};

/** @note Maximum Password Age */
const NumberOfDaysField = ({
    value,
    onChange,
    isDisabled,
    error,
    walkthroughId,
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    return (
        <FormControl sx={{ margin: '0 !important' }} isInvalid={!!error}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling(true)}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Maximum Password Age{' '}
                        <span style={{ color: '#e53e3e' }}>*</span>
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        Enter the number of days before passwords expire.
                    </Text>
                </Box>
                <Box flex="1" minWidth="450px">
                    <VStack width="110%" alignItems="normal" sx={inputStyles}>
                        <Field
                            component={NumberField}
                            name="numberOfDays"
                            id="numberOfDays"
                            value={value}
                            onChange={onChange}
                            isDisabled={isDisabled}
                            placeholder="Please enter maximum password age in days"
                            data-walkthroughid={walkthroughId}
                            validate={(value) =>
                                !value && 'Maximum Password Age is required'
                            }
                            error={error}
                        />
                    </VStack>
                    {error && <FieldError pl={0}>{error}</FieldError>}
                </Box>
            </Stack>
        </FormControl>
    );
};

/** @note Password Reuse Limit */
const PasswordReuseLimitField = ({
    value,
    onChange,
    isDisabled,
    error,
    walkthroughId,
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    return (
        <FormControl sx={{ margin: '0 !important' }} isInvalid={!!error}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling(true)}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Password Reuse Limit{' '}
                        <span style={{ color: '#e53e3e' }}>*</span>
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        Specify how many previous passwords cannot be reused.
                    </Text>
                </Box>
                <Box flex="1" minWidth="450px">
                    <VStack width="110%" alignItems="normal" sx={inputStyles}>
                        <Field
                            component={NumberField}
                            name="reuseLimit"
                            id="reuseLimit"
                            value={value}
                            onChange={onChange}
                            isDisabled={isDisabled}
                            placeholder="Please enter password reuse limit"
                            data-walkthroughid={walkthroughId}
                            validate={(value) =>
                                !value && 'Password Reuse Limit is required'
                            }
                            error={error}
                        />
                    </VStack>
                    {error && <FieldError pl={0}>{error}</FieldError>}
                </Box>
            </Stack>
        </FormControl>
    );
};

/** @note Minimum Password Age */
const MinPasswordAgeField = ({
    value,
    onChange,
    isDisabled,
    error,
    walkthroughId,
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    return (
        <FormControl
            sx={{ margin: '0 0 12px 0!important' }}
            isInvalid={!!error}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling(true)}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Minimum Password Age{' '}
                        <span style={{ color: '#e53e3e' }}>*</span>
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        Enter the minimum number of days between password
                        changes.
                    </Text>
                </Box>
                <Box flex="1" minWidth="450px">
                    <VStack width="110%" alignItems="normal" sx={inputStyles}>
                        <Field
                            component={NumberField}
                            name="minPasswordAge"
                            id="minPasswordAge"
                            value={value}
                            onChange={onChange}
                            isDisabled={isDisabled}
                            placeholder="Please enter minimum password age in days"
                            data-walkthroughid={walkthroughId}
                            validate={(value) =>
                                !value && 'Minimum Password Age is required'
                            }
                            error={error}
                        />
                    </VStack>
                    {error && <FieldError pl={0}>{error}</FieldError>}
                </Box>
            </Stack>
        </FormControl>
    );
};
export default function PasswordPolicy() {
    const [isLoading, setIsLoading] = useState(true);
    const [isSwitchDisabled, setIsSwitchDisabled] = useState(false);

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });

    const initialState = {
        isEnabled: false,
        numberOfDays: '180',
        reuseLimit: '5',
        minPasswordAge: '0',
    };

    const [passwordPolicy, setPasswordPolicy] = useState(initialState);
    const [errors, setErrors] = useState(null);

    // use query for getting password policy
    useQuery({
        queryKey: ['passwordPolicy'],
        queryFn: () => getPasswordPolicy(currentCompany),
        enabled: !!currentCompany, // Ensures the query is only executed when currentCompany is truthy
        onSuccess: (data) => {
            setPasswordPolicy({ ...data });
            setIsLoading(false);
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message);
            setIsLoading(false);
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    // enable/disable password policy
    const handlePasswordCheckbox = async (value) => {
        setErrors(null);
        setIsSwitchDisabled(true);

        // when switch is turned off (value = false), call endpoint to disable password expiration policy
        try {
            if (!value && passwordPolicy?.id) {
                var pp = {
                    id: passwordPolicy.id,
                    companyID: currentCompany,
                    numberOfDays: passwordPolicy?.numberOfDays || 0,
                    reuseLimit: passwordPolicy?.reuseLimit || 0,
                    minPasswordAge: passwordPolicy?.minPasswordAge || 0,
                    isEnabled: false,
                };

                const result = await updatePasswordPolicy(pp);
                setPasswordPolicy({ ...result.data });
                toast.success(
                    'Password expiration policy has been disabled successfully!',
                );
            }
            setPasswordPolicy({
                ...passwordPolicy,
                isEnabled: value,
            });
        } catch (e) {
            toast.error(e?.response?.data?.message);
        } finally {
            setIsSwitchDisabled(false);
        }
    };

    // Prepare initial values for FormBase
    const initialValues = useMemo(
        () => ({
            isEnabled: passwordPolicy.isEnabled || false,
            numberOfDays: passwordPolicy.numberOfDays || '180',
            reuseLimit: passwordPolicy.reuseLimit || '5',
            minPasswordAge: passwordPolicy.minPasswordAge || '0',
        }),
        [passwordPolicy],
    );

    // Handle FormBase submit - receives Formik values
    const handleFormSubmit = (values) => {
        // Use the Formik values for the API call
        handlePasswordPolicyWithValues(values);
    };

    // create/update password policy using provided values
    const handlePasswordPolicyWithValues = async (values) => {
        // validation check, number of days cannot be empty or undefined
        if (!values?.numberOfDays || values.numberOfDays === '') {
            return setErrors({
                ...errors,
                numberOfDays: 'Field is required.',
            });
        } else if (!values?.reuseLimit || values.reuseLimit === '') {
            return setErrors({
                ...errors,
                reuseLimit: 'Field is required.',
            });
        } else if (!values?.minPasswordAge || values.minPasswordAge === '') {
            return setErrors({
                ...errors,
                minPasswordAge: 'Field is required.',
            });
        }

        setIsLoading(true);
        setErrors(null);
        var pp = {
            id: passwordPolicy?.id,
            companyID: currentCompany,
            numberOfDays: values.numberOfDays,
            reuseLimit: values.reuseLimit,
            minPasswordAge: values.minPasswordAge,
            isEnabled: values.isEnabled,
        };

        try {
            if (passwordPolicy?.id) {
                const data = await updatePasswordPolicy(pp);
                setPasswordPolicy({ ...data });
                toast.success(
                    'Password expiration policy has been updated successfully!',
                );
            } else {
                const data = await postPasswordPolicy(pp);
                setPasswordPolicy({ ...data });
                toast.success(
                    'Password expiration policy has been enabled successfully!',
                );
            }
        } catch (e) {
            toast.error(e?.response?.data?.message);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <Stack>
                <HStack width="24%" mb={3}>
                    <SkeletonCircle w={9} mr={1} size={5} />
                    <Skeleton w={334} h={5} />
                </HStack>

                <HStack>
                    <VStack w="100%" style={{ WebkitAlignItems: 'normal' }}>
                        <HStack width="60%">
                            <Skeleton border="10px" flex={1} h={6} />
                        </HStack>
                        <HStack mb={1}>
                            <Skeleton border="10px" w="100%" h={9} mr={3} />
                        </HStack>
                        <HStack width="9%" mb={1}>
                            <Skeleton flex={1} h={5} />
                        </HStack>
                    </VStack>

                    <VStack w="100%" style={{ WebkitAlignItems: 'normal' }}>
                        <HStack width="50%">
                            <Skeleton border="10px" flex={1} h={6} />
                        </HStack>
                        <HStack mb={1}>
                            <Skeleton border="10px" w="100%" h={9} mr={3} />
                        </HStack>
                        <HStack width="9%" mb={1}>
                            <Skeleton flex={1} h={5} />
                        </HStack>
                    </VStack>

                    <VStack w="100%" style={{ WebkitAlignItems: 'normal' }}>
                        <HStack width="53%">
                            <Skeleton border="10px" flex={1} h={6} />
                        </HStack>
                        <HStack mb={1}>
                            <Skeleton border="10px" w="100%" h={9} mr={3} />
                        </HStack>
                        <HStack width="9%" mb={1}>
                            <Skeleton flex={1} h={5} />
                        </HStack>
                    </VStack>
                </HStack>

                <VStack mt="16px !important">
                    <Skeleton border="10px" w="4%" h={9} />
                </VStack>
            </Stack>
        );
    }

    return (
        <FormBase
            noBackBtn={true}
            initialValues={initialValues}
            finalFormSubmit={handleFormSubmit}
            ufSaveBtn={walkthroughIds.adminPasswordPolicy.ufSaveBtn}
            isSubmitDisabled={
                isLoading ||
                isSwitchDisabled ||
                (errors &&
                    Object.values(errors).some((e) => e?.trim().length > 0))
            }>
            {({ setFieldValue, values }) => {
                return [
                    // Switch Control Field
                    <PasswordExpirySwitchField
                        key="switch-control"
                        isChecked={values.isEnabled}
                        isDisabled={isSwitchDisabled || isLoading}
                        onChange={(checked) => {
                            setFieldValue('isEnabled', checked);
                            handlePasswordCheckbox(checked);
                        }}
                        walkthroughId={
                            walkthroughIds.adminPasswordPolicy
                                .ufPasswordPolicySwitch
                        }
                    />,
                    <NumberOfDaysField
                        key="numberOfDays"
                        value={values.numberOfDays}
                        onChange={(e) => {
                            const newValue = e.target.value;

                            if (newValue === '') {
                                setErrors({
                                    ...errors,
                                    numberOfDays: 'Field is required.',
                                });
                                setFieldValue('numberOfDays', newValue);
                                setPasswordPolicy({
                                    ...passwordPolicy,
                                    numberOfDays: newValue,
                                });
                            } else if (
                                /^[1-9]\d*$/.test(newValue) &&
                                newValue.length <= 3
                            ) {
                                const numValue = parseInt(newValue, 10);

                                if (
                                    numValue < MINNUMBEROFDAYS ||
                                    numValue > MAXNUMBEROFDAYS
                                ) {
                                    setErrors({
                                        ...errors,
                                        numberOfDays:
                                            'Maximum Password Age must be between 14 and 365 days.',
                                    });
                                } else {
                                    setErrors({
                                        ...errors,
                                        numberOfDays: '',
                                    });
                                }

                                setFieldValue('numberOfDays', newValue);
                                setPasswordPolicy({
                                    ...passwordPolicy,
                                    numberOfDays: newValue,
                                });
                            }
                        }}
                        isDisabled={
                            isLoading || isSwitchDisabled || !values.isEnabled
                        }
                        error={errors?.numberOfDays}
                        walkthroughId={
                            walkthroughIds.adminPasswordPolicy
                                .ufNumberOfDaysInput
                        }
                    />,
                    <PasswordReuseLimitField
                        key="reuseLimit"
                        value={values.reuseLimit}
                        onChange={(e) => {
                            const newValue = e.target.value;

                            if (newValue === '') {
                                setErrors({
                                    ...errors,
                                    reuseLimit: 'Field is required.',
                                });
                                setFieldValue('reuseLimit', newValue);
                                setPasswordPolicy({
                                    ...passwordPolicy,
                                    reuseLimit: newValue,
                                });
                            } else if (
                                /^(0|[1-9]\d*)$/.test(newValue) &&
                                newValue.length <= 3
                            ) {
                                const numValue = parseInt(newValue, 10);

                                if (
                                    numValue >= MINREUSELIMIT &&
                                    numValue <= MAXREUSELIMIT
                                ) {
                                    setErrors({
                                        ...errors,
                                        reuseLimit: '',
                                    });
                                } else {
                                    setErrors({
                                        ...errors,
                                        reuseLimit:
                                            'Password Reuse Limit must be between 0 and 100.',
                                    });
                                }

                                setFieldValue('reuseLimit', newValue);
                                setPasswordPolicy({
                                    ...passwordPolicy,
                                    reuseLimit: newValue,
                                });
                            }
                        }}
                        isDisabled={
                            isLoading || isSwitchDisabled || !values.isEnabled
                        }
                        error={errors?.reuseLimit}
                        walkthroughId={
                            walkthroughIds.adminPasswordPolicy
                                .ufPasswordReuseLimitInput
                        }
                    />,
                    // Always render inputs but disable them when policy is not enabled

                    <MinPasswordAgeField
                        key="minPasswordAge"
                        value={values.minPasswordAge}
                        onChange={(e) => {
                            const newValue = e.target.value;

                            if (newValue === '') {
                                setErrors({
                                    ...errors,
                                    minPasswordAge: 'Field is required.',
                                });
                                setFieldValue('minPasswordAge', newValue);
                                setPasswordPolicy({
                                    ...passwordPolicy,
                                    minPasswordAge: newValue,
                                });
                            } else if (
                                /^(0|[1-9]\d*)$/.test(newValue) &&
                                newValue.length <= 2
                            ) {
                                const numValue = parseInt(newValue, 10);

                                if (
                                    numValue < MINMINPASSWORDAGE ||
                                    numValue > MAXMINPASSWORDAGE
                                ) {
                                    setErrors({
                                        ...errors,
                                        minPasswordAge:
                                            'Minimum Password Age must be between 0 and 10 days.',
                                    });
                                } else {
                                    setErrors({
                                        ...errors,
                                        minPasswordAge: '',
                                    });
                                }

                                setFieldValue('minPasswordAge', newValue);
                                setPasswordPolicy({
                                    ...passwordPolicy,
                                    minPasswordAge: newValue,
                                });
                            }
                        }}
                        isDisabled={
                            isLoading || isSwitchDisabled || !values.isEnabled
                        }
                        error={errors?.minPasswordAge}
                        walkthroughId={
                            walkthroughIds.adminPasswordPolicy
                                .ufMinPasswordAgeInput
                        }
                    />,
                ];
            }}
        </FormBase>
    );
}
